import { ContentfulService } from '../services/contentfulClient';

import AccordionItem from './accordionItemEntity';
import BreadcrumbsPath from './breadcrumbsPathEntity';
import CertificateContentPage from './certificatesContentPageEntity';
import CompetencesContentPage from './competencesPageEntity';
import ContentPage from './contentPageEntity';
import VttFooter from './footerEntity';
import LinkItem from './linkItemEntity';

// -----------------------------------------------------------------------------
// Content type IDs
// -----------------------------------------------------------------------------
const CONTENT_PAGE_TYPE = 'contentPage';
const CERTIFICATES_PAGE_TYPE = 'contentPageCertificatesList';
const COMPETENCES_PAGE_TYPE = 'competencesPage';
const FOOTER_TYPE = 'vttFooter';

// -----------------------------------------------------------------------------
// Certificates page
// -----------------------------------------------------------------------------
export const fetchCertificateContentPageAsync = (query: object = {}): Promise<CertificateContentPage> => {
  const client = new ContentfulService().cdaClient;
  return client
    .getEntries({ content_type: CERTIFICATES_PAGE_TYPE, ...query })
    .then(res => res.items[0])
    .then(mapToCertificateContentPage);
};

// -----------------------------------------------------------------------------
// Competences page
// -----------------------------------------------------------------------------
export const fetchCompetencesContentPageAsync = (query: object = {}): Promise<CompetencesContentPage> => {
  const client = new ContentfulService().cdaClient;
  return client
    .getEntries({ content_type: COMPETENCES_PAGE_TYPE, ...query })
    .then(res => (res.items.length > 0 ? res.items[0] : null))
    .then(item => (item ? mapToCompetencesContentPage(item) : new CompetencesContentPage()))
    .catch(() => new CompetencesContentPage()); // network error fallback
};

// -----------------------------------------------------------------------------
// Generic content page by friendlyUrl
// -----------------------------------------------------------------------------
export const fetchContentPageByIdAsync = (id: string): Promise<ContentPage> => {
  const client = new ContentfulService().cdaClient;
  return client
    .getEntries({
      content_type: CONTENT_PAGE_TYPE,
      'fields.friendlyUrl': id,
      include: 3,
    })
    .then(res => res.items[0])
    .then(mapToContentPage);
};

// -----------------------------------------------------------------------------
// Footer
// -----------------------------------------------------------------------------
export const fetchFooterAsync = (query: object = {}): Promise<VttFooter> => {
  const client = new ContentfulService().cdaClient;
  return client
    .getEntries({ content_type: FOOTER_TYPE, ...query })
    .then(res => res.items[0])
    .then(mapToFooter);
};

// -----------------------------------------------------------------------------
// Mapping helpers
// -----------------------------------------------------------------------------
const mapToContentPage = (entry: any): ContentPage => {
  const page = new ContentPage();

  page.id = entry.fields.friendlyUrl;
  page.title = entry.fields.title;

  if (entry.fields.heroImage) {
    page.heroImage = entry.fields.heroImage.fields.file.url;
  }
  if (entry.fields.content) {
    page.content = entry.fields.content;
  }
  if (entry.fields.accordionItems) {
    page.accordionItems = entry.fields.accordionItems.map(mapToAccordionItem);
  }
  if (entry.fields.links) {
    page.linkItems = entry.fields.links.map(mapToLinkItem);
  }
  if (entry.fields.breadcrumbsPath) {
    page.breadcrumbsPath = mapToBreadcrumbsPathItem(entry.fields.breadcrumbsPath);
  }

  return page;
};

const mapToCertificateContentPage = (entry: any): CertificateContentPage => {
  const page = new CertificateContentPage();

  page.id = entry.fields.friendlyUrl;
  page.title = entry.fields.title;

  if (entry.fields.heroImage) {
    page.heroImage = entry.fields.heroImage.fields.file.url;
  }
  if (entry.fields.content) {
    page.content = entry.fields.content;
  }
  if (entry.fields.builderLinks) {
    page.builderLinks = entry.fields.builderLinks.map(mapToLinkItem);
  }
  if (entry.fields.professionalLinks) {
    page.professionalLinks = entry.fields.professionalLinks.map(mapToLinkItem);
  }
  if (entry.fields.links) {
    page.linkItems = entry.fields.links.map(mapToLinkItem);
  }
  if (entry.fields.breadcrumbsPath) {
    page.breadcrumbsPath = mapToBreadcrumbsPathItem(entry.fields.breadcrumbsPath);
  }

  return page;
};

const mapToCompetencesContentPage = (entry: any): CompetencesContentPage => {
  const page = new CompetencesContentPage();

  page.id = entry.fields.friendlyUrl;
  page.title = entry.fields.title;

  if (entry.fields.heroImage) {
    page.heroImage = entry.fields.heroImage.fields.file.url;
  }
  if (entry.fields.content) {
    page.content = entry.fields.content;
  }
  if (entry.fields.plannerLinkList) {
    page.plannerLinkList = entry.fields.plannerLinkList.map(mapToLinkItem);
  }
  if (entry.fields.supervisorLinkList) {
    page.supervisorLinkList = entry.fields.supervisorLinkList.map(mapToLinkItem);
  }
  if (entry.fields.links) {
    page.linkItems = entry.fields.links.map(mapToLinkItem);
  }
  if (entry.fields.breadcrumbsPath) {
    page.breadcrumbsPath = mapToBreadcrumbsPathItem(entry.fields.breadcrumbsPath);
  }

  return page;
};

const mapToAccordionItem = (item: any): AccordionItem => {
  const acc = new AccordionItem();
  acc.id = item.sys.id;
  acc.title = item.fields.title;
  acc.content = item.fields.content;
  return acc;
};

const mapToBreadcrumbsPathItem = (item: any): BreadcrumbsPath => {
  const bc = new BreadcrumbsPath();
  bc.id = item.sys.id;
  bc.title = item.fields.title;
  bc.relativePath = item.fields.relativePath;
  if (item.fields.parentBredcrumbsItem) {
    bc.parentBredcrumbsItem = mapToBreadcrumbsPathItem(item.fields.parentBredcrumbsItem);
  }
  return bc;
};

const mapToLinkItem = (item: any): LinkItem => {
  const link = new LinkItem();
  link.id = item.sys.id;
  link.title = item.fields.title;
  // Change internal Urls to absolute to avoid issues with relative paths
  const rawUrl = item.fields.url || '';
  const isExternal = rawUrl.startsWith('http') || rawUrl.startsWith('//');
  link.url = (isExternal || rawUrl.startsWith('/')) ? rawUrl : '/' + rawUrl;
  if (item.fields.description) {
    link.description = item.fields.description;
  }
  if (item.fields.urlTarget) {
    link.urlTarget = item.fields.urlTarget;
  }
  return link;
};

const mapToFooter = (entry: any): VttFooter => {
  const footer = new VttFooter();
  footer.id = entry.fields.friendlyUrl;
  footer.content = entry.fields.leftColumnContent;
  if (entry.fields.logoImage) {
    footer.logoImage = entry.fields.logoImage.fields.file.url;
  }
  return footer;
};

// -----------------------------------------------------------------------------
// Public API object
// -----------------------------------------------------------------------------
export const contentManager = {
  fetchCertificateContentPageAsync,
  fetchCompetencesContentPageAsync,
  fetchContentPageByIdAsync,
  fetchFooterAsync,
};



// WEBPACK FOOTER //
// ./src/api/contentManager.ts